import env from "src/env";
import React, { useEffect, useState } from "react";
import { OAuthProvider } from "modules/session";

import { Button, FormRow, Icon, Divider, Link } from "frontend-components";

const DASHBOARD_URL = window.location.origin;
const OAUTH_BASE_URL = `${env.VITE_AUTH_URL}/oauth/start`;

export const defaultOAuthProviders: Array<OAuthProvider> = ["github"];
export const allOAuthProviders: Array<OAuthProvider> = [
  ...defaultOAuthProviders,
  "netlify",
];

const handleSubmit = (
  providerName: string,
  sessionType: "oauth" | "dashboard"
) => {
  const oauthUrl =
    OAUTH_BASE_URL +
    `?provider_name=${providerName}` +
    `&redirect_url=${DASHBOARD_URL}/auth/oauth/callback` +
    `&session_type=${sessionType}`;
  window.location.href = oauthUrl;
};

/**
 * OAuthFormFragement is a component that renders a form with OAuth provider
 * buttons. It can be used in register and login flows.
 *
 * @param submitText - The text to display on the submit button
 * @param sessionType - The type of session to create
 * @param preferredOAuthProviders - The list of OAuth providers to display by default
 * @param enabledOAuthProviders - The list of OAuth providers that are enabled and will
 * be rendered after an additional CTA
 */
export const OAuthFormFragment = ({
  submitText = "Login",
  sessionType = "dashboard",
  preferredOAuthProviders = defaultOAuthProviders,
  enabledOAuthProviders = allOAuthProviders,
}: {
  submitText?: string;
  sessionType?: "oauth" | "dashboard";
  preferredOAuthProviders?: Array<OAuthProvider>;
  enabledOAuthProviders?: Array<OAuthProvider>;
}) => {
  const [showAllOAuthProviders, setShowAllOAuthProviders] = useState(false);
  const [allowedOAuthProviders, setAllowedOAuthProviders] = useState(
    preferredOAuthProviders
  );

  const toggleShowAllOAuthProviders = () => {
    setShowAllOAuthProviders((prev) => !prev);
  };

  useEffect(() => {
    if (showAllOAuthProviders) setAllowedOAuthProviders(enabledOAuthProviders);
    else setAllowedOAuthProviders(preferredOAuthProviders);
  }, [showAllOAuthProviders]);

  const renderProviders = () => {
    return allowedOAuthProviders.length ? (
      allowedOAuthProviders.map((provider) => {
        if (provider === "github")
          return (
            <GithubOAuthFormFragment key={provider} sessionType={sessionType} />
          );
        if (provider === "netlify")
          return (
            <NetlifyOAuthFormFragment
              key={provider}
              sessionType={sessionType}
            />
          );
      })
    ) : (
      <></>
    );
  };

  return (
    <React.Fragment>
      <FormRow margin="sm">
        <Divider margin="xl" />
      </FormRow>
      {renderProviders()}
      {preferredOAuthProviders.length !== enabledOAuthProviders.length && (
        <FormRow margin="none" horizontalAlign="center">
          <Link href={undefined} onClick={toggleShowAllOAuthProviders}>
            {showAllOAuthProviders
              ? `Hide other ${submitText.toLocaleLowerCase()} options`
              : `See other ${submitText.toLocaleLowerCase()} options`}
          </Link>
        </FormRow>
      )}
    </React.Fragment>
  );
};

export const GithubOAuthFormFragment = ({
  sessionType,
}: {
  sessionType: "dashboard" | "oauth";
}) => {
  return (
    <FormRow margin="sm">
      <Button
        id="github-button"
        type="button"
        theme="github"
        fullscreen={true}
        icon={
          <Icon
            size="xl"
            theme="inherit"
            baseClassName="fa-brands"
            iconClassName="fa-github"
          />
        }
        onClick={() => handleSubmit("github", sessionType)}
        casing="capitalize"
      >
        GitHub
      </Button>
    </FormRow>
  );
};

export const NetlifyOAuthFormFragment = ({
  sessionType,
}: {
  sessionType: "dashboard" | "oauth";
}) => {
  return (
    <FormRow margin="sm">
      <Button
        id="netlify-button"
        type="button"
        theme="netlify"
        fullscreen={true}
        icon={<Icon size="xl" theme="inherit" iconClassName="netlify" />}
        onClick={() => handleSubmit("netlify", sessionType)}
        casing="capitalize"
      >
        Netlify
      </Button>
    </FormRow>
  );
};
